/**
 * Established-session `/me` reconcile (admin `usePeriodicMeValidation` parity).
 *
 * Identity GET `/customer-account/me` is refresh-denylisted on ApiClient so guest
 * boot cannot `/me`→refresh storm. Callers that already hold an in-memory session
 * (or F5/boot `cookieRestore`) must rotate the still-valid refresh cookie when
 * the ~15m access cookie dies, then retry `/me` — never treat missing-cred as
 * revoke first. Guest: one refresh POST then hard miss → `end`.
 */

import type { CustomerMeDTO } from '../../features/account/accountTypes.js';
import type { RuntimeConfig } from '../../config/runtimeConfig.js';
import {
  fetchPlatformMe,
  type FetchPlatformMeOptions,
  type PlatformMeResult,
} from './fetchPlatformMe.js';
import {
  attemptSessionRefresh,
  isHardSessionRefreshRevocation,
  shouldAttemptRefreshOnMissingCredential,
} from './sessionRefresh.js';

export type EstablishedMeReconcile =
  | { readonly action: 'ok'; readonly me: CustomerMeDTO }
  | { readonly action: 'keep' }
  | { readonly action: 'end' };

export type ReconcileEstablishedPlatformMeOptions = {
  readonly signal?: AbortSignal;
  /**
   * Caller already has `{ customerId }` in memory (periodic `/me`, Account gate).
   * Rotate even if the established latch raced clear.
   */
  readonly hasMemorySession?: boolean;
  /**
   * F5 / cold boot: RAM latch is gone but the refresh cookie may still be valid.
   * Rotate dying access once; do not require `hasMemorySession` or the latch.
   */
  readonly cookieRestore?: boolean;
};

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

async function probeMe(
  config: Pick<RuntimeConfig, 'apiBaseUrl'>,
  signal: AbortSignal | undefined,
  extra?: FetchPlatformMeOptions,
): Promise<PlatformMeResult> {
  return fetchPlatformMe(config, signal, extra);
}

function shouldRotateDyingAccess(
  hasMemorySession: boolean,
  cookieRestore: boolean,
): boolean {
  return (
    hasMemorySession ||
    cookieRestore === true ||
    shouldAttemptRefreshOnMissingCredential()
  );
}

/**
 * Probe `/me`; on missing-cred/unauthorized for an established SPA, POST refresh
 * then `forceFresh` `/me`. Soft refresh failures keep the session; hard
 * revocation (and a still-dead `/me` after a successful rotate) end it.
 */
export async function reconcileEstablishedPlatformMe(
  config: RuntimeConfig,
  options?: ReconcileEstablishedPlatformMeOptions,
): Promise<EstablishedMeReconcile> {
  const signal = options?.signal;
  const hasMemorySession = options?.hasMemorySession === true;
  const cookieRestore = options?.cookieRestore === true;

  let first: PlatformMeResult;
  try {
    first = await probeMe(config, signal);
  } catch (err) {
    if (isAbortError(err)) {
      throw err;
    }
    return { action: 'keep' };
  }

  if (first.kind === 'ok') {
    return { action: 'ok', me: first.me };
  }
  if (first.kind === 'ceiling' || first.kind === 'transient') {
    return { action: 'keep' };
  }

  if (!shouldRotateDyingAccess(hasMemorySession, cookieRestore)) {
    return { action: 'end' };
  }

  const refreshed = await attemptSessionRefresh(config);
  if (refreshed.ok) {
    let retry: PlatformMeResult;
    try {
      retry = await probeMe(config, signal, { forceFresh: true });
    } catch (err) {
      if (isAbortError(err)) {
        throw err;
      }
      return { action: 'keep' };
    }
    if (retry.kind === 'ok') {
      return { action: 'ok', me: retry.me };
    }
    if (retry.kind === 'ceiling' || retry.kind === 'transient') {
      return { action: 'keep' };
    }
    return { action: 'end' };
  }

  if (isHardSessionRefreshRevocation(refreshed.code)) {
    return { action: 'end' };
  }

  return { action: 'keep' };
}
